/**
 * Friendly empty-list wording per ListView `orderType`. Lists not listed here fall
 * back to generic wording.
 *
 * @const {!Object<string, {
 *   plural: string,
 *   firstRun: string,
 *   create?: {label: string, to: string},
 *   secondary?: {label: string, to: string},
 * }>}
 */
export const EMPTY_STATE_COPY = {
  salesOrder: {
    plural: "sales orders",
    firstRun: "Create your first sales order, or connect Shopify to bring orders in automatically.",
    create: { label: "Create sales order", to: "/sales/order/create" },
    secondary: { label: "Connect Shopify", to: "/integration/shopify" },
  },
  purchase: {
    plural: "purchase orders",
    firstRun: "Raise a purchase order to a supplier and follow it all the way to receipt.",
    create: { label: "Create purchase order", to: "/orders/purchase/create" },
  },
  transfer: {
    plural: "transfer orders",
    firstRun: "Move stock between your warehouses with a transfer order.",
    create: { label: "Create transfer order", to: "/transfer_order/create" },
  },
  shipment: {
    plural: "shipments",
    firstRun: "Shipments show up here once a purchase order or transfer order is on its way.",
  },
  saleInvoice: {
    plural: "invoices",
    firstRun: "Invoice a customer straight from a sales order, or create one by hand.",
    create: { label: "Create invoice", to: "/sale_invoice/create" },
  },
  pricelist: {
    plural: "price lists",
    firstRun:
      "Price lists set what your customers pay. Pick one on a sales order and line prices fill in for you.",
    create: { label: "Create price list", to: "/sales/pricelist/create" },
  },
  product: {
    plural: "products",
    firstRun: "Add the products you buy and sell, or import them from Shopify.",
    create: { label: "Create product", to: "/master/product/create" },
  },
  customer: {
    plural: "customers",
    firstRun: "Add the businesses you sell to, then pick them when you create a sales order.",
    create: { label: "Create customer", to: "/master/customer/create" },
  },
  supplier: {
    plural: "suppliers",
    firstRun: "Add the suppliers you buy from so you can raise purchase orders.",
    create: { label: "Create supplier", to: "/master/supplier/create" },
  },
};

const LIST_ONLY_PARAMS = new Set(["limit", "offset", "ordering", "view_id"]);

/**
 * True when the list request carries anything that narrows results, such as a
 * column filter or the toolbar search.
 *
 * @param {?Object<string, *>} params
 * @return {boolean}
 */
export const hasActiveListFilters = params =>
  Object.entries(params || {}).some(
    ([key, value]) =>
      !LIST_ONLY_PARAMS.has(key) && value !== undefined && value !== null && value !== "",
  );

/**
 * Keeps paging size, sort and the saved view; drops every filter.
 *
 * @param {?Object<string, *>} params
 * @return {!Object<string, *>}
 */
export const clearListFilters = params => {
  const kept = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (LIST_ONLY_PARAMS.has(key)) kept[key] = value;
  });
  return { ...kept, offset: 0 };
};

/**
 * @param {?{id?: *, value?: string}} view
 * @return {boolean}
 */
const isSavedView = view => Boolean(view?.id) && view?.value !== "Default View";

/**
 * Works out why a list is empty and what to say about it.
 *
 * @param {{
 *   orderType: string,
 *   params: ?Object<string, *>,
 *   view: ?{id?: *, value?: string},
 *   viewOnly: boolean,
 *   onClearFilters: function(),
 * }} args
 * @return {{
 *   variant: string,
 *   title: string,
 *   description: string,
 *   actions: !Array<{label: string, to?: string, onClick?: function(), primary?: boolean}>,
 * }}
 */
export const getEmptyStateContent = ({ orderType, params, view, viewOnly, onClearFilters }) => {
  const copy = EMPTY_STATE_COPY[orderType];
  const plural = copy?.plural || "records";

  if (hasActiveListFilters(params)) {
    return {
      variant: "filtered",
      title: `No ${plural} match these filters`,
      description: "Try a different search, or clear the filters to see everything.",
      actions: [{ label: "Clear filters", onClick: onClearFilters, primary: true }],
    };
  }

  if (isSavedView(view)) {
    return {
      variant: "view",
      title: "All caught up",
      description: `There are no ${plural} in this view right now.`,
      actions: [],
    };
  }

  if (!copy) {
    return { variant: "generic", title: "Nothing here yet", description: "", actions: [] };
  }

  const actions = viewOnly
    ? []
    : [copy.create && { ...copy.create, primary: true }, copy.secondary].filter(Boolean);
  return {
    variant: "firstRun",
    title: `No ${plural} yet`,
    description: copy.firstRun,
    actions,
  };
};
